import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { NotificationContainer } from '../components/ui/Notification';
import { BulkEmployeeImportModal } from '../components/modals/BulkEmployeeImportModal';
import { UserIcon, UsersIcon, SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon, MailIcon, PhoneIcon, BriefcaseIcon, CheckIcon, XIcon, AlertCircleIcon, DownloadIcon, UploadIcon, ArrowUpIcon, ArrowDownIcon, InfoIcon } from 'lucide-react';
import { mockEmployees, mockExpenses } from '../utils/mockData';
export function Employees() {
  // State for employees and filtering
  const [employees, setEmployees] = useState([...mockEmployees]);
  const [filteredEmployees, setFilteredEmployees] = useState([...mockEmployees]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  // State for modals
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isEditEmployeeModalOpen, setIsEditEmployeeModalOpen] = useState(false);
  const [isDeleteEmployeeModalOpen, setIsDeleteEmployeeModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  // State for notifications
  const [notifications, setNotifications] = useState([]);
  // State for new/edit employee form
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    email: '',
    phone: '',
    department: 'Engineering',
    position: '',
    role: 'employee'
  });
  // Form errors
  const [formErrors, setFormErrors] = useState({});
  // Get unique departments for filter
  const departments = [...new Set(mockEmployees.map(emp => emp.department))];
  // Filter and sort employees
  useEffect(() => {
    let filtered = [...employees];
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(employee => employee.name.toLowerCase().includes(search) || employee.email.toLowerCase().includes(search) || employee.department.toLowerCase().includes(search) || employee.position.toLowerCase().includes(search));
    }
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(employee => employee.department === departmentFilter);
    }
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredEmployees(filtered);
  }, [employees, searchTerm, departmentFilter, sortField, sortDirection]);
  // Get employee expenses
  const getEmployeeExpenses = employeeId => {
    return mockExpenses.filter(expense => expense.employeeId === employeeId);
  };
  // Get pending expenses count
  const getPendingExpensesCount = employeeId => {
    return mockExpenses.filter(expense => expense.employeeId === employeeId && expense.status === 'pending').length;
  };
  // Handle sort
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Open add employee modal
  const openAddEmployeeModal = () => {
    setEmployeeForm({
      name: '',
      email: '',
      phone: '',
      department: 'Engineering',
      position: '',
      role: 'employee'
    });
    setFormErrors({});
    setIsAddEmployeeModalOpen(true);
  };
  // Open edit employee modal
  const openEditEmployeeModal = employee => {
    setSelectedEmployee(employee);
    setEmployeeForm({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      department: employee.department,
      position: employee.position || '',
      role: employee.role || 'employee'
    });
    setFormErrors({});
    setIsEditEmployeeModalOpen(true);
  };
  // Open delete employee modal
  const openDeleteEmployeeModal = employee => {
    setSelectedEmployee(employee);
    setIsDeleteEmployeeModalOpen(true);
  };
  // Open bulk import modal
  const openBulkImportModal = () => {
    setIsBulkImportModalOpen(true);
  };
  // Handle form change
  const handleFormChange = e => {
    const {
      name,
      value
    } = e.target;
    setEmployeeForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!employeeForm.name.trim()) errors.name = 'Name is required';
    if (!employeeForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(employeeForm.email)) {
      errors.email = 'Email is invalid';
    }
    if (!employeeForm.department.trim()) errors.department = 'Department is required';
    if (!employeeForm.position.trim()) errors.position = 'Position is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle add employee
  const handleAddEmployee = () => {
    if (!validateForm()) return;
    const newEmployee = {
      id: employees.length > 0 ? Math.max(...employees.map(e => e.id)) + 1 : 1,
      ...employeeForm,
      hireDate: new Date().toISOString().split('T')[0]
    };
    setEmployees([...employees, newEmployee]);
    setIsAddEmployeeModalOpen(false);
    // Add notification
    addNotification({
      type: 'success',
      title: 'Employee Added',
      message: `${newEmployee.name} has been added successfully.`
    });
  };
  // Handle edit employee
  const handleEditEmployee = () => {
    if (!validateForm()) return;
    const updatedEmployees = employees.map(emp => emp.id === selectedEmployee.id ? {
      ...emp,
      ...employeeForm
    } : emp);
    setEmployees(updatedEmployees);
    setIsEditEmployeeModalOpen(false);
    // Add notification
    addNotification({
      type: 'success',
      title: 'Employee Updated',
      message: `${employeeForm.name}'s information has been updated.`
    });
  };
  // Handle delete employee
  const handleDeleteEmployee = () => {
    setEmployees(employees.filter(emp => emp.id !== selectedEmployee.id));
    setIsDeleteEmployeeModalOpen(false);
    // Add notification
    addNotification({
      type: 'info',
      title: 'Employee Removed',
      message: `${selectedEmployee.name} has been removed from the system.`
    });
  };
  // Handle bulk import
  const handleBulkImport = importedEmployees => {
    // Get the highest existing ID
    const maxId = employees.length > 0 ? Math.max(...employees.map(e => e.id)) : 0;
    // Add IDs to imported employees if they don't have them
    const employeesWithIds = importedEmployees.map((emp, index) => ({
      ...emp,
      id: emp.id || maxId + index + 1,
      hireDate: emp.hireDate || new Date().toISOString().split('T')[0]
    }));
    // Add new employees to existing ones
    setEmployees([...employees, ...employeesWithIds]);
    // Add notification
    addNotification({
      type: 'success',
      title: 'Bulk Import Completed',
      message: `${employeesWithIds.length} employees have been imported successfully.`
    });
  };
  // Add notification
  const addNotification = notification => {
    const id = Date.now();
    setNotifications(prev => [...prev, {
      id,
      ...notification
    }]);
  };
  // Remove notification
  const removeNotification = id => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  return <div className="space-y-6" data-id="element-1486">
      {/* Notifications */}
      <NotificationContainer notifications={notifications} onClose={removeNotification} data-id="element-1487" />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1488">
        <h1 className="text-2xl font-bold text-gray-100" data-id="element-1489">Employees</h1>
        <div className="flex flex-wrap gap-2" data-id="element-1490">
          <Button variant="outline" data-id="element-1491">
            <DownloadIcon size={16} className="mr-1.5" data-id="element-1492" />
            Export
          </Button>
          <Button variant="outline" onClick={openBulkImportModal} data-id="element-1493">
            <UploadIcon size={16} className="mr-1.5" data-id="element-1494" />
            Bulk Import
          </Button>
          <Button onClick={openAddEmployeeModal} data-id="element-1495">
            <PlusIcon size={16} className="mr-1.5" data-id="element-1496" />
            Add Employee
          </Button>
        </div>
      </div>
      {/* Filters */}
      <Card className="p-4" data-id="element-1497">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-1498">
          <div className="relative flex-1" data-id="element-1499">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1500">
              <SearchIcon size={16} className="text-gray-400" data-id="element-1501" />
            </div>
            <input type="text" placeholder="Search employees by name, email, or department..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1502" />
          </div>
          <div className="w-full md:w-64" data-id="element-1503">
            <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1504">
              <option value="all" data-id="element-1505">All Departments</option>
              {departments.map((dept, index) => <option key={index} value={dept} data-id="element-1506">
                  {dept}
                </option>)}
            </select>
          </div>
        </div>
      </Card>
      {/* Employees Table */}
      <Card data-id="element-1507">
        <div className="overflow-x-auto" data-id="element-1508">
          <table className="min-w-full divide-y divide-gray-700" data-id="element-1509">
            <thead className="bg-gray-800" data-id="element-1510">
              <tr data-id="element-1511">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-1512">
                  <div className="flex items-center" data-id="element-1513">
                    <span data-id="element-1514">Employee</span>
                    {sortField === 'name' && <span className="ml-1" data-id="element-1515">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1516" /> : <ArrowDownIcon size={14} data-id="element-1517" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('department')} data-id="element-1518">
                  <div className="flex items-center" data-id="element-1519">
                    <span data-id="element-1520">Department</span>
                    {sortField === 'department' && <span className="ml-1" data-id="element-1521">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1522" /> : <ArrowDownIcon size={14} data-id="element-1523" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('position')} data-id="element-1524">
                  <div className="flex items-center" data-id="element-1525">
                    <span data-id="element-1526">Position</span>
                    {sortField === 'position' && <span className="ml-1" data-id="element-1527">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1528" /> : <ArrowDownIcon size={14} data-id="element-1529" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('role')} data-id="element-1530">
                  <div className="flex items-center" data-id="element-1531">
                    <span data-id="element-1532">Role</span>
                    {sortField === 'role' && <span className="ml-1" data-id="element-1533">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1534" /> : <ArrowDownIcon size={14} data-id="element-1535" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1536">
                  Expenses
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1537">
                  Pending
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1538">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700" data-id="element-1539">
              {filteredEmployees.length > 0 ? filteredEmployees.map(employee => <tr key={employee.id} className="hover:bg-gray-700 transition-colors" data-id="element-1540">
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1541">
                      <div className="flex items-center" data-id="element-1542">
                        <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-600 flex items-center justify-center text-sm text-gray-300" data-id="element-1543">
                          {employee.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="ml-3" data-id="element-1544">
                          <div className="text-sm font-medium text-gray-200" data-id="element-1545">
                            {employee.name}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center mt-0.5" data-id="element-1546">
                            <MailIcon size={12} className="mr-1 flex-shrink-0" data-id="element-1547" />
                            {employee.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300" data-id="element-1548">
                      {employee.department}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300" data-id="element-1549">
                      {employee.position}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1550">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${employee.role === 'admin' ? 'bg-purple-900 text-purple-200' : 'bg-gray-700 text-gray-300'}`} data-id="element-1551">
                        {employee.role === 'admin' ? 'Admin' : 'Employee'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-300" data-id="element-1552">
                      {getEmployeeExpenses(employee.id).length}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center" data-id="element-1553">
                      {getPendingExpensesCount(employee.id) > 0 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-900 text-yellow-200" data-id="element-1554">
                          {getPendingExpensesCount(employee.id)}
                        </span> : <span className="text-sm text-gray-500" data-id="element-1555">0</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1556">
                      <div className="flex items-center justify-end space-x-2" data-id="element-1557">
                        <button onClick={() => openEditEmployeeModal(employee)} className="text-gray-400 hover:text-gray-200 hover:bg-gray-600 p-1.5 rounded transition-colors" title="Edit Employee" aria-label="Edit Employee" data-id="element-1558">
                          <EditIcon size={16} data-id="element-1559" />
                        </button>
                        <button onClick={() => openDeleteEmployeeModal(employee)} className="text-gray-400 hover:text-red-300 hover:bg-red-900/30 p-1.5 rounded transition-colors" title="Delete Employee" aria-label="Delete Employee" data-id="element-1560">
                          <TrashIcon size={16} data-id="element-1561" />
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-1562">
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400" data-id="element-1563">
                    {searchTerm || departmentFilter !== 'all' ? 'No employees match your search criteria' : 'No employees found'}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {filteredEmployees.length > 0 && <div className="px-4 py-3 border-t border-gray-700 flex justify-between items-center" data-id="element-1564">
            <div className="text-sm text-gray-400" data-id="element-1565">
              Showing {filteredEmployees.length} of {employees.length} employees
            </div>
          </div>}
      </Card>
      {/* Add Employee Modal */}
      <Modal isOpen={isAddEmployeeModalOpen} onClose={() => setIsAddEmployeeModalOpen(false)} title="Add New Employee" data-id="element-1566">
        <div className="p-6" data-id="element-1567">
          <div className="grid grid-cols-1 gap-4" data-id="element-1568">
            <div data-id="element-1569">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1570">
                Full Name <span className="text-red-500" data-id="element-1571">*</span>
              </label>
              <div className="relative" data-id="element-1572">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1573">
                  <UserIcon size={16} className="text-gray-400" data-id="element-1574" />
                </div>
                <input type="text" name="name" value={employeeForm.name} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.name ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="John Doe" data-id="element-1575" />
              </div>
              {formErrors.name && <p className="mt-1 text-sm text-red-500" data-id="element-1576">{formErrors.name}</p>}
            </div>
            <div data-id="element-1577">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1578">
                Email Address <span className="text-red-500" data-id="element-1579">*</span>
              </label>
              <div className="relative" data-id="element-1580">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1581">
                  <MailIcon size={16} className="text-gray-400" data-id="element-1582" />
                </div>
                <input type="email" name="email" value={employeeForm.email} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.email ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="john.doe@example.com" data-id="element-1583" />
              </div>
              {formErrors.email && <p className="mt-1 text-sm text-red-500" data-id="element-1584">{formErrors.email}</p>}
            </div>
            <div data-id="element-1585">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1586">
                Phone Number
              </label>
              <div className="relative" data-id="element-1587">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1588">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-1589" />
                </div>
                <input type="text" name="phone" value={employeeForm.phone} onChange={handleFormChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" placeholder="(555) 123-4567" data-id="element-1590" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1591">
              <div data-id="element-1592">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1593">
                  Department <span className="text-red-500" data-id="element-1594">*</span>
                </label>
                <select name="department" value={employeeForm.department} onChange={handleFormChange} className={`w-full px-3 py-2 bg-gray-700 border ${formErrors.department ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1595">
                  <option value="Engineering" data-id="element-1596">Engineering</option>
                  <option value="Marketing" data-id="element-1597">Marketing</option>
                  <option value="Finance" data-id="element-1598">Finance</option>
                  <option value="HR" data-id="element-1599">HR</option>
                  <option value="Sales" data-id="element-1600">Sales</option>
                  <option value="Product" data-id="element-1601">Product</option>
                  <option value="Operations" data-id="element-1602">Operations</option>
                </select>
                {formErrors.department && <p className="mt-1 text-sm text-red-500" data-id="element-1603">
                    {formErrors.department}
                  </p>}
              </div>
              <div data-id="element-1604">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1605">
                  Position <span className="text-red-500" data-id="element-1606">*</span>
                </label>
                <div className="relative" data-id="element-1607">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1608">
                    <BriefcaseIcon size={16} className="text-gray-400" data-id="element-1609" />
                  </div>
                  <input type="text" name="position" value={employeeForm.position} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.position ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="Software Engineer" data-id="element-1610" />
                </div>
                {formErrors.position && <p className="mt-1 text-sm text-red-500" data-id="element-1611">
                    {formErrors.position}
                  </p>}
              </div>
            </div>
            <div data-id="element-1612">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1613">
                Role
              </label>
              <div className="mt-1 space-y-2" data-id="element-1614">
                <div className="flex items-center" data-id="element-1615">
                  <input id="role-employee" name="role" type="radio" value="employee" checked={employeeForm.role === 'employee'} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1616" />
                  <label htmlFor="role-employee" className="ml-2 text-sm text-gray-200" data-id="element-1617">
                    Employee (Standard user with expense submission privileges)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1618">
                  <input id="role-admin" name="role" type="radio" value="admin" checked={employeeForm.role === 'admin'} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1619" />
                  <label htmlFor="role-admin" className="ml-2 text-sm text-gray-200" data-id="element-1620">
                    Admin (Full access to all system features and settings)
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-750 px-6 py-4 flex justify-end border-t border-gray-700" data-id="element-1621">
          <div className="flex space-x-3" data-id="element-1622">
            <Button variant="outline" onClick={() => setIsAddEmployeeModalOpen(false)} data-id="element-1623">
              Cancel
            </Button>
            <Button onClick={handleAddEmployee} data-id="element-1624">
              <PlusIcon size={16} className="mr-1.5" data-id="element-1625" />
              Add Employee
            </Button>
          </div>
        </div>
      </Modal>
      {/* Edit Employee Modal */}
      <Modal isOpen={isEditEmployeeModalOpen} onClose={() => setIsEditEmployeeModalOpen(false)} title="Edit Employee" data-id="element-1626">
        <div className="p-6" data-id="element-1627">
          <div className="grid grid-cols-1 gap-4" data-id="element-1628">
            <div data-id="element-1629">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1630">
                Full Name <span className="text-red-500" data-id="element-1631">*</span>
              </label>
              <div className="relative" data-id="element-1632">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1633">
                  <UserIcon size={16} className="text-gray-400" data-id="element-1634" />
                </div>
                <input type="text" name="name" value={employeeForm.name} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.name ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1635" />
              </div>
              {formErrors.name && <p className="mt-1 text-sm text-red-500" data-id="element-1636">{formErrors.name}</p>}
            </div>
            <div data-id="element-1637">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1638">
                Email Address <span className="text-red-500" data-id="element-1639">*</span>
              </label>
              <div className="relative" data-id="element-1640">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1641">
                  <MailIcon size={16} className="text-gray-400" data-id="element-1642" />
                </div>
                <input type="email" name="email" value={employeeForm.email} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.email ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1643" />
              </div>
              {formErrors.email && <p className="mt-1 text-sm text-red-500" data-id="element-1644">{formErrors.email}</p>}
            </div>
            <div data-id="element-1645">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1646">
                Phone Number
              </label>
              <div className="relative" data-id="element-1647">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1648">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-1649" />
                </div>
                <input type="text" name="phone" value={employeeForm.phone} onChange={handleFormChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1650" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1651">
              <div data-id="element-1652">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1653">
                  Department <span className="text-red-500" data-id="element-1654">*</span>
                </label>
                <select name="department" value={employeeForm.department} onChange={handleFormChange} className={`w-full px-3 py-2 bg-gray-700 border ${formErrors.department ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1655">
                  <option value="Engineering" data-id="element-1656">Engineering</option>
                  <option value="Marketing" data-id="element-1657">Marketing</option>
                  <option value="Finance" data-id="element-1658">Finance</option>
                  <option value="HR" data-id="element-1659">HR</option>
                  <option value="Sales" data-id="element-1660">Sales</option>
                  <option value="Product" data-id="element-1661">Product</option>
                  <option value="Operations" data-id="element-1662">Operations</option>
                </select>
                {formErrors.department && <p className="mt-1 text-sm text-red-500" data-id="element-1663">
                    {formErrors.department}
                  </p>}
              </div>
              <div data-id="element-1664">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1665">
                  Position <span className="text-red-500" data-id="element-1666">*</span>
                </label>
                <div className="relative" data-id="element-1667">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1668">
                    <BriefcaseIcon size={16} className="text-gray-400" data-id="element-1669" />
                  </div>
                  <input type="text" name="position" value={employeeForm.position} onChange={handleFormChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${formErrors.position ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1670" />
                </div>
                {formErrors.position && <p className="mt-1 text-sm text-red-500" data-id="element-1671">
                    {formErrors.position}
                  </p>}
              </div>
            </div>
            <div data-id="element-1672">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1673">
                Role
              </label>
              <div className="mt-1 space-y-2" data-id="element-1674">
                <div className="flex items-center" data-id="element-1675">
                  <input id="edit-role-employee" name="role" type="radio" value="employee" checked={employeeForm.role === 'employee'} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1676" />
                  <label htmlFor="edit-role-employee" className="ml-2 text-sm text-gray-200" data-id="element-1677">
                    Employee (Standard user with expense submission privileges)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1678">
                  <input id="edit-role-admin" name="role" type="radio" value="admin" checked={employeeForm.role === 'admin'} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1679" />
                  <label htmlFor="edit-role-admin" className="ml-2 text-sm text-gray-200" data-id="element-1680">
                    Admin (Full access to all system features and settings)
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-750 px-6 py-4 flex justify-end border-t border-gray-700" data-id="element-1681">
          <div className="flex space-x-3" data-id="element-1682">
            <Button variant="outline" onClick={() => setIsEditEmployeeModalOpen(false)} data-id="element-1683">
              Cancel
            </Button>
            <Button onClick={handleEditEmployee} data-id="element-1684">
              <CheckIcon size={16} className="mr-1.5" data-id="element-1685" />
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
      {/* Delete Employee Modal */}
      <Modal isOpen={isDeleteEmployeeModalOpen} onClose={() => setIsDeleteEmployeeModalOpen(false)} title="Delete Employee" size="sm" data-id="element-1686">
        <div className="p-6" data-id="element-1687">
          <div className="flex items-center justify-center mb-4 text-red-500" data-id="element-1688">
            <AlertCircleIcon size={48} data-id="element-1689" />
          </div>
          <h3 className="text-lg font-medium text-center text-gray-100 mb-2" data-id="element-1690">
            Are you sure you want to delete this employee?
          </h3>
          <p className="text-center text-gray-400 mb-6" data-id="element-1691">
            This will permanently remove {selectedEmployee?.name} from the
            system. This action cannot be undone.
          </p>
          <div className="flex justify-center space-x-3" data-id="element-1692">
            <Button variant="outline" onClick={() => setIsDeleteEmployeeModalOpen(false)} data-id="element-1693">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteEmployee} data-id="element-1694">
              Delete Employee
            </Button>
          </div>
        </div>
      </Modal>
      {/* Bulk Import Modal */}
      <BulkEmployeeImportModal isOpen={isBulkImportModalOpen} onClose={() => setIsBulkImportModalOpen(false)} onImport={handleBulkImport} data-id="element-1695" />
    </div>;
}